// §2.2: For routine antepartum visits, auto-populates Reason for Visit from chart data.

import { Box } from '@chakra-ui/react'
import { FormElement } from '@components/forms/Input'
import { useProfile } from '@contexts/user/ProfileContext'
import {
  Assessment,
  getCorrectedValue,
  getGestationalAgeDays,
  reasonForVisitField,
} from '@hb/shared'
import { useEffect, useMemo } from 'react'
import { useField, useForm } from 'react-final-form'
import { SectionLabel } from '../../SectionLabel'
import { formatGa } from '../../utils'

// Provider confirms or edits. Chief complaint lives in SOAP S section for non-routine visits.
export const ReasonForVisitRow = () => {
  const {
    assessments,
    assessmentId,
    user,
    selectedAssessment: { populated: assessment },
  } = useProfile()
  const {
    input: { value: encounterDate },
  } = useField<string>('date', { subscription: { value: true } })
  const {
    input: { value: currentReasonForVisit },
  } = useField<string | undefined>('data.reasonForVisit', { subscription: { value: true } })
  const form = useForm()

  const edd = useMemo(
    () => getCorrectedValue(assessment as Assessment | null, 'delivery.edd') as number | undefined,
    [assessment],
  )

  const gestationalAgeDays = useMemo(
    () => getGestationalAgeDays(edd, encounterDate),
    [edd, encounterDate],
  )

  const { gravida, para } = useMemo(() => {
    const pastAssessments = Object.entries(assessments ?? {})
      .filter(([id]) => id !== assessmentId)
      .map(([, a]) => a)
    const additionalPregnancies =
      (user as any)?.gyn?.mergedData?.gynAndSexualHistory?.gynHistory?.additionalPregnancies ?? []
    let totalPast = 0
    let paraCount = 0
    for (const a of pastAssessments) {
      totalPast++
      const outcome = (a.corrections?.pregnancyOutcome ?? a.data?.pregnancyOutcome) as
        | string
        | undefined
      if (outcome !== 'miscarriage' && outcome !== 'abortion') paraCount++
    }
    for (const p of additionalPregnancies) {
      totalPast++
      const outcome = p.pregnancyOutcome as string | undefined
      if (
        outcome === 'born-living' ||
        outcome === 'born-with-complications' ||
        outcome === 'stillborn'
      )
        paraCount++
    }
    return { gravida: totalPast + 1, para: paraCount }
  }, [assessments, assessmentId, user])

  const autoValue = useMemo(() => {
    const gaPart = gestationalAgeDays != null ? `GA: ${formatGa(gestationalAgeDays)}` : null
    const gpPart = `G${gravida}P${para}`
    return [gaPart, gpPart].filter(Boolean).join(' — ')
  }, [gestationalAgeDays, gravida, para])

  useEffect(() => {
    if (!currentReasonForVisit && autoValue) {
      form.change('data.reasonForVisit', autoValue)
    }
  }, [autoValue, currentReasonForVisit, form])

  return (
    <Box data-child-name="data.reasonForVisit" w="100%" px={5} py="14px">
      <SectionLabel>Reason for Visit</SectionLabel>
      <FormElement showName={false} field={reasonForVisitField} name="data.reasonForVisit" />
    </Box>
  )
}
